import React, { useEffect, useRef } from 'react';
import _ from 'lodash';
import { useAppContext, ACTIONS } from '@components/Contexts/AppContext';
import ResponsiveRenderer from '@src/components/SiteRenderer/ResponsiveRenderer';
import ClassicRenderer from '@src/components/SiteRenderer/ClassicRenderer';
import { getViewerNewPageId, getVisitorId } from '@src/utils/frameHelpers';

import * as Sentry from '@sentry/browser';
import createBiLogger from '@src/utils/biLogger';

const SiteRenderer = () => {
  const {
    dispatch,
    state: { isSiteResponsive, loading, metaSiteId },
  } = useAppContext();

  const ready = useRef<boolean>(false);

  useEffect(() => {
    if (loading) {
      ready.current = false;
    }
  }, [loading]);

  function siteReady(previewEl: React.MutableRefObject<HTMLIFrameElement>) {
    const visitorId = getVisitorId(previewEl);
    if (!visitorId) {
      dispatch({ type: ACTIONS.DISABLE_TOOL });
      return;
    }
    ready.current = true;
    dispatch({
      type: ACTIONS.SET_LOADING,
      payload: false,
    });

    dispatch({
      type: ACTIONS.SET_VIEWER_ELEMENT,
      payload: {
        element: previewEl.current,
      },
    });
    dispatch({
      type: ACTIONS.SET_BI_LOGGER,
      payload: createBiLogger(getVisitorId(previewEl), metaSiteId),
    });
    getViewerNewPageId(
      previewEl.current.contentDocument!,
      (newPageId: string) => {
        dispatch({
          type: ACTIONS.SET_CURRENT_PAGE_ID,
          payload: newPageId,
        });
      },
    );
  }

  async function handleFrameLoaded(
    previewEl: React.MutableRefObject<HTMLIFrameElement>,
  ) {
    try {
      previewEl.current.contentDocument!.addEventListener(
        'feedback-csr-ready',
        () => {
          siteReady(previewEl);
        },
        false,
      );
      _.delay(() => {
        if (!ready.current) {
          siteReady(previewEl);
        }
      }, 3000);
    } catch (e) {
      Sentry.captureException(e);
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: {
          showError: true,
          errorMsgLocalKey: 'get_feedback_error',
        },
      });
    }
  }

  return isSiteResponsive ? (
    <ResponsiveRenderer handleFrameLoaded={handleFrameLoaded} />
  ) : (
    <ClassicRenderer handleFrameLoaded={handleFrameLoaded} />
  );
};

export default SiteRenderer;
